/**
 * Reporte: Cupones no pagados — Fase 7.3 (reporte 1 de 7)
 *
 * Lista cupones en estado pendiente o vencido con monto, antigüedad y
 * empresa correspondiente. Permite filtrar por empresa, estado y rango
 * de fechas de vencimiento. Exporta a PDF y Excel.
 *
 * Documentos relacionados:
 *   - requerimientos.md § M13 (Reportes)
 *   - DECISIONES.md #034 (stack reportes cliente)
 */

import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Card,
  Button,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import { useCupones } from '../../hooks/useCupones';
import { useEmpresas } from '../../hooks/useEmpresas';
import { formatCurrency, formatDate, formatCuit } from '../../utils/format';
import {
  type Cupon,
  type CuponEstado,
  getEstadoCuponLabel,
  getEstadoCuponColor,
} from '../../types/cupon';

import {
  createReportePdf,
  addReporteTable,
  addReporteFooter,
  fmtCurrency,
  fmtDate,
  fmtText,
  type ColumnaPdf,
} from '../../utils/reportes/pdf';
import { generarReporteExcel, type ColumnaExcel } from '../../utils/reportes/excel';
import { triggerBlobDownload, buildReporteFilename } from '../../utils/reportes/download';

// ── Tipos auxiliares ──────────────────────────────────────────────────────────

interface CuponNoPagadoRow {
  id: string;
  codigo: string;
  empresa_razon_social: string;
  empresa_cuit: string;
  periodo: string;
  fecha_emision: string;
  fecha_vencimiento: string;
  dias_mora: number;
  monto_aporte: number;
  interes: number;
  monto_total: number;
  estado: CuponEstado;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function diasDesdeVencimiento(fechaVencimiento: string): number {
  const venc = new Date(fechaVencimiento);
  const hoy = new Date();
  const diffMs = hoy.getTime() - venc.getTime();
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

function cuponToRow(c: Cupon): CuponNoPagadoRow {
  return {
    id: c.id,
    codigo: c.codigo,
    empresa_razon_social: c.empresa?.razon_social ?? '—',
    empresa_cuit: c.empresa?.cuit ?? '',
    periodo: c.periodo?.periodo ?? '—',
    fecha_emision: c.fecha_emision,
    fecha_vencimiento: c.fecha_vencimiento,
    dias_mora: diasDesdeVencimiento(c.fecha_vencimiento),
    monto_aporte: Number(c.monto_aporte),
    interes: Number(c.interes),
    monto_total: Number(c.monto_total),
    estado: c.estado,
  };
}

// ── Componente ────────────────────────────────────────────────────────────────

export default function CuponesNoPagadosReporte() {
  // ── Datos ──────────────────────────────────────────────────────────────
  const { data: cupones = [], isLoading, isError } = useCupones();
  const { data: empresasResult } = useEmpresas({ pageSize: 1000 });
  const empresas = empresasResult?.data ?? [];

  // ── Filtros ────────────────────────────────────────────────────────────
  const [empresaId, setEmpresaId] = useState<string | null>(null);
  const [incluirPendientes, setIncluirPendientes] = useState(true);
  const [incluirVencidos, setIncluirVencidos] = useState(true);
  const [vencDesde, setVencDesde] = useState('');
  const [vencHasta, setVencHasta] = useState('');

  // ── Rows filtradas ─────────────────────────────────────────────────────
  const rows: CuponNoPagadoRow[] = useMemo(() => {
    const estadosIncluidos: CuponEstado[] = [];
    if (incluirPendientes) estadosIncluidos.push('pendiente');
    if (incluirVencidos) estadosIncluidos.push('vencido');

    return cupones
      .filter((c) => estadosIncluidos.includes(c.estado))
      .filter((c) => (empresaId ? c.empresa_id === empresaId : true))
      .filter((c) => (vencDesde ? c.fecha_vencimiento >= vencDesde : true))
      .filter((c) => (vencHasta ? c.fecha_vencimiento <= vencHasta : true))
      .map(cuponToRow)
      .sort((a, b) => b.dias_mora - a.dias_mora); // los más viejos primero
  }, [cupones, empresaId, incluirPendientes, incluirVencidos, vencDesde, vencHasta]);

  // ── Totales ────────────────────────────────────────────────────────────
  const totales = useMemo(
    () => ({
      cantidad: rows.length,
      totalAdeudado: rows.reduce((sum, r) => sum + r.monto_total, 0),
      totalAporte: rows.reduce((sum, r) => sum + r.monto_aporte, 0),
      totalInteres: rows.reduce((sum, r) => sum + r.interes, 0),
    }),
    [rows],
  );

  // ── Subtítulo dinámico para el reporte exportado ────────────────────────
  const subtitulo = useMemo(() => {
    const partes: string[] = [];
    if (empresaId) {
      const e = empresas.find((x) => x.id === empresaId);
      if (e) partes.push(`Empresa: ${e.razon_social}`);
    }
    const estados: string[] = [];
    if (incluirPendientes) estados.push('Pendientes');
    if (incluirVencidos) estados.push('Vencidos');
    if (estados.length > 0) partes.push(`Estado: ${estados.join(' + ')}`);
    if (vencDesde) partes.push(`Vencimiento desde: ${formatDate(vencDesde)}`);
    if (vencHasta) partes.push(`Vencimiento hasta: ${formatDate(vencHasta)}`);
    return partes.join(' · ');
  }, [empresaId, empresas, incluirPendientes, incluirVencidos, vencDesde, vencHasta]);

  // ── Export PDF ─────────────────────────────────────────────────────────
  const handleExportPdf = () => {
    const doc = createReportePdf({
      titulo: 'Cupones no pagados',
      subtitulo,
      orientacion: 'landscape',
    });

    const columnas: ColumnaPdf[] = [
      { header: 'Empresa', format: (v) => fmtText(v) },
      { header: 'Código', format: (v) => fmtText(v) },
      { header: 'Período', align: 'center', format: (v) => fmtText(v) },
      { header: 'Vencimiento', align: 'center', format: (v) => fmtDate(v) },
      { header: 'Días mora', align: 'right', format: (v) => fmtText(v) },
      { header: 'Aporte', align: 'right', format: (v) => fmtCurrency(v) },
      { header: 'Interés', align: 'right', format: (v) => fmtCurrency(v) },
      { header: 'Total', align: 'right', format: (v) => fmtCurrency(v) },
    ];

    const body = rows.map((r) => [
      r.empresa_razon_social,
      r.codigo,
      r.periodo,
      r.fecha_vencimiento,
      r.dias_mora,
      r.monto_aporte,
      r.interes,
      r.monto_total,
    ]);

    // Agregamos una fila de totales al final.
    body.push([
      `TOTAL (${totales.cantidad} cupones)`,
      '',
      '',
      '',
      '',
      totales.totalAporte,
      totales.totalInteres,
      totales.totalAdeudado,
    ]);

    addReporteTable(doc, columnas, body, {
      titulo: 'Cupones no pagados',
      subtitulo,
    });
    addReporteFooter(doc);

    const blob = doc.output('blob');
    triggerBlobDownload(blob, buildReporteFilename('cupones-no-pagados', 'pdf'));
  };

  // ── Export Excel ────────────────────────────────────────────────────────
  const handleExportExcel = async () => {
    const columnas: ColumnaExcel[] = [
      { header: 'Empresa', key: 'empresa', width: 35, format: 'text' },
      { header: 'CUIT', key: 'cuit', width: 14, format: 'text' },
      { header: 'Código', key: 'codigo', width: 28, format: 'text' },
      { header: 'Período', key: 'periodo', width: 10, format: 'text' },
      { header: 'Emisión', key: 'emision', width: 12, format: 'date' },
      { header: 'Vencimiento', key: 'vencimiento', width: 12, format: 'date' },
      { header: 'Días mora', key: 'diasMora', width: 10, format: 'integer' },
      { header: 'Aporte', key: 'aporte', width: 14, format: 'currency' },
      { header: 'Interés', key: 'interes', width: 14, format: 'currency' },
      { header: 'Total', key: 'total', width: 14, format: 'currency' },
      { header: 'Estado', key: 'estado', width: 12, format: 'text' },
    ];

    const rowsExcel = rows.map((r) => ({
      empresa: r.empresa_razon_social,
      cuit: formatCuit(r.empresa_cuit),
      codigo: r.codigo,
      periodo: r.periodo,
      emision: new Date(r.fecha_emision),
      vencimiento: new Date(r.fecha_vencimiento),
      diasMora: r.dias_mora,
      aporte: r.monto_aporte,
      interes: r.interes,
      total: r.monto_total,
      estado: getEstadoCuponLabel(r.estado),
    }));

    const blob = await generarReporteExcel({
      titulo: 'Cupones no pagados',
      subtitulo,
      nombreHoja: 'Cupones no pagados',
      columnas,
      rows: rowsExcel,
    });
    triggerBlobDownload(blob, buildReporteFilename('cupones-no-pagados', 'xlsx'));
  };

  // ── DataGrid columns ────────────────────────────────────────────────────
  const columns: GridColDef<CuponNoPagadoRow>[] = [
    {
      field: 'empresa_razon_social',
      headerName: 'Empresa',
      flex: 2,
      minWidth: 200,
    },
    {
      field: 'codigo',
      headerName: 'Código',
      width: 220,
      renderCell: (p: GridRenderCellParams<CuponNoPagadoRow>) => (
        <Typography variant="body2" fontFamily="monospace" fontSize={13}>
          {p.row.codigo}
        </Typography>
      ),
    },
    { field: 'periodo', headerName: 'Período', width: 100 },
    {
      field: 'fecha_vencimiento',
      headerName: 'Vencimiento',
      width: 120,
      renderCell: (p: GridRenderCellParams<CuponNoPagadoRow>) => (
        <Typography
          variant="body2"
          color={p.row.estado === 'vencido' ? 'error' : 'inherit'}
        >
          {formatDate(p.row.fecha_vencimiento)}
        </Typography>
      ),
    },
    {
      field: 'dias_mora',
      headerName: 'Días mora',
      width: 100,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'monto_aporte',
      headerName: 'Aporte',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<CuponNoPagadoRow>) =>
        formatCurrency(p.row.monto_aporte),
    },
    {
      field: 'interes',
      headerName: 'Interés',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<CuponNoPagadoRow>) => (
        <Typography
          variant="body2"
          color={p.row.interes > 0 ? 'error' : 'inherit'}
        >
          {formatCurrency(p.row.interes)}
        </Typography>
      ),
    },
    {
      field: 'monto_total',
      headerName: 'Total',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<CuponNoPagadoRow>) => (
        <Typography variant="body2" fontWeight={600}>
          {formatCurrency(p.row.monto_total)}
        </Typography>
      ),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 110,
      renderCell: (p: GridRenderCellParams<CuponNoPagadoRow>) => (
        <Chip
          label={getEstadoCuponLabel(p.row.estado)}
          color={getEstadoCuponColor(p.row.estado)}
          size="small"
        />
      ),
    },
  ];

  const sinEstadoSeleccionado = !incluirPendientes && !incluirVencidos;
  const sinDatos = !isLoading && !isError && rows.length === 0;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to="/reportes"
        sx={{ mb: 2 }}
      >
        Volver a Reportes
      </Button>

      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <ReceiptLongIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Cupones no pagados
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cupones pendientes o vencidos con monto, antigüedad y empresa.
          </Typography>
        </Box>
      </Stack>

      {/* Filtros */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Filtros
        </Typography>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          flexWrap="wrap"
          useFlexGap
        >
          <Autocomplete
            sx={{ minWidth: 280 }}
            size="small"
            options={empresas}
            getOptionLabel={(opt) => opt.razon_social}
            value={empresas.find((e) => e.id === empresaId) ?? null}
            onChange={(_, val) => setEmpresaId(val?.id ?? null)}
            renderInput={(params) => (
              <TextField {...params} label="Empresa (todas si vacío)" />
            )}
          />

          <TextField
            type="date"
            size="small"
            label="Vencimiento desde"
            value={vencDesde}
            onChange={(e) => setVencDesde(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <TextField
            type="date"
            size="small"
            label="Vencimiento hasta"
            value={vencHasta}
            onChange={(e) => setVencHasta(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />

          <Stack direction="row" spacing={1}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={incluirPendientes}
                  onChange={(e) => setIncluirPendientes(e.target.checked)}
                />
              }
              label="Pendientes"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={incluirVencidos}
                  onChange={(e) => setIncluirVencidos(e.target.checked)}
                />
              }
              label="Vencidos"
            />
          </Stack>
        </Stack>
      </Card>

      {/* Totales + Export buttons */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        mb={2}
      >
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Cantidad
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {totales.cantidad}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total adeudado
            </Typography>
            <Typography variant="h6" fontWeight={700} color="warning.main">
              {formatCurrency(totales.totalAdeudado)}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPdf}
            disabled={rows.length === 0}
          >
            Descargar PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableViewIcon />}
            onClick={handleExportExcel}
            disabled={rows.length === 0}
          >
            Descargar Excel
          </Button>
        </Stack>
      </Stack>

      {/* Estado / errores */}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar los cupones.
        </Alert>
      )}
      {sinEstadoSeleccionado && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Tenés que seleccionar al menos un estado (Pendientes o Vencidos).
        </Alert>
      )}
      {sinDatos && !sinEstadoSeleccionado && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No hay cupones que cumplan los filtros seleccionados.
        </Alert>
      )}

      {/* Tabla preview */}
      <Card>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
        />
      </Card>
    </Box>
  );
}
